// @ts-check

import { v4 as uuidv4 } from 'uuid'
import { computed, ref } from 'vue'

/**
 * Type representing the type of toast message.
 * @typedef {'success'|'info'|'danger'} ToastType
 */

/**
 * Type representing a toast message.
 * @typedef {Object} Toast
 * @property {ToastType} type - The type of the toast message.
 * @property {string} content - The content of the toast message.
 * @property {number} [duration] - The duration of the toast message, optional.
 */

/**
 * Type representing a toast message with an id.
 * @typedef {Toast & { id: string }} ToastWithId
 */

/**
 * Type representing options for toast composable.
 * @typedef {Object} ToastOptions
 * @property {number} [limit] - How many toasts to pick from top of the queue for display, optional.
 */

/** @type {import('vue').Ref<ToastWithId[]>} */
const toastQueue = ref([])

/** @type {import('vue').Ref<number>} */
const numToasts = computed(() => toastQueue.value.length)

/**
 * Removes toast based on the given id from the toast queue.
 * @param {ToastWithId['id']} idToRemove - The id of the toast to remove.
 */
const removeToast = (idToRemove) => {
  toastQueue.value = toastQueue.value.filter(({ id }) => id !== idToRemove)
}

/**
 * Adds the given toast to the toast queue.
 * @param {Toast} toast
 * @returns {ToastWithId}
 */
const addToast = (toast) => {
  const newToast = { ...toast, id: uuidv4() }
  toastQueue.value = [...toastQueue.value, newToast]
  return newToast
}

/**
 * Creates a new toast adder for the given type.
 * @param {ToastType} type
 * @param {number} [duration]
 * @returns {(toast: Omit<Toast, 'type'>) => ToastWithId}
 */
const toastAdder = (type, duration) => (toast) =>
  addToast({ duration, ...toast, type })

/**
 * Adds a success toast with a default duration of 5000.
 */
const addSuccessToast = toastAdder('success', 5000)

/**
 * Adds an info toast.
 */
const addInfoToast = toastAdder('info')

/**
 * Adds a danger toast.
 */
const addDangerToast = toastAdder('danger')

/**
 * Clears all toasts.
 */
const clearToasts = () => {
  toastQueue.value = []
}

/**
 * @param {ToastOptions} [options]
 */
export const useToasts = ({ limit = 3 } = {}) => {
  const toasts = computed(() => toastQueue.value.slice(0, limit))

  const numHiddenToasts = computed(() =>
    Math.max(toastQueue.value.length - limit, 0)
  )

  return {
    addDangerToast,
    addInfoToast,
    addSuccessToast,
    addToast,
    clearToasts,
    numHiddenToasts,
    numToasts,
    removeToast,
    toasts
  }
}

export default useToasts
